import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { History, AlertTriangle, Edit, FileText } from "lucide-react";

const HistoryDialog = ({ open, onOpenChange, recordHistory }) => {
  if (!open) return null;
  
  const hasHistory = recordHistory && recordHistory.edit_history && recordHistory.edit_history.length > 0;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-indigo-600" />
            Edit History
          </DialogTitle>
        </DialogHeader>
        
        {recordHistory ? (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <RecordInfoSection data={recordHistory} />
            {hasHistory ? (
              <HistoryEntriesList entries={recordHistory.edit_history} />
            ) : (
              <EmptyState />
            )}
          </div>
        ) : null}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function RecordInfoSection({ data }) {
  return (
    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
      <p className="text-sm font-medium">{data.user_name || "Unknown"}</p>
      <p className="text-xs text-slate-500">{data.date || ""}</p>
      
      {data.auto_punch_out ? (
        <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded">
          <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Auto Punch-Out Applied
          </p>
          {data.auto_punch_out_remark ? (
            <p className="text-xs text-amber-600 mt-1">{data.auto_punch_out_remark}</p>
          ) : null}
        </div>
      ) : null}
      
      {data.admin_corrected ? (
        <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
          <p className="text-xs text-blue-700 dark:text-blue-400 flex items-center gap-1">
            <Edit className="w-3 h-3" />
            Admin Corrected
          </p>
          {data.correction_remark ? (
            <p className="text-xs text-blue-600 mt-1">{data.correction_remark}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function HistoryEntriesList({ entries }) {
  return (
    <div className="space-y-3">
      {entries.map((entry, idx) => (
        <HistoryEntryCard key={idx} entry={entry} />
      ))}
    </div>
  );
}

function HistoryEntryCard({ entry }) {
  const isAutoType = entry.edit_type === "auto_punch_out";
  const dateStr = entry.edited_at ? new Date(entry.edited_at).toLocaleString("en-IN") : "";
  const editorName = entry.edited_by_name || entry.edited_by || "Unknown";
  
  return (
    <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <Badge variant={isAutoType ? "secondary" : "default"}>
          {isAutoType ? "Auto Punch-Out" : "Manual Correction"}
        </Badge>
        <span className="text-xs text-slate-500">{dateStr}</span>
      </div>
      <p className="text-xs text-slate-600 dark:text-slate-400">By: {editorName}</p>
      {entry.remark ? (
        <p className="text-xs text-slate-500 mt-1 italic">{entry.remark}</p>
      ) : null}
      <ChangesDisplay changes={entry.changes} />
    </div>
  );
}

function ChangesDisplay({ changes }) {
  if (!changes) return null;
  
  const punchOutChange = changes.punch_out_time;
  const hoursChange = changes.working_hours;
  const statusChange = changes.status;
  
  return (
    <div className="mt-2 text-xs space-y-1">
      {punchOutChange ? (
        <ChangeItem 
          label="Punch Out" 
          oldVal={punchOutChange.old || "None"} 
          newVal={punchOutChange.new || ""} 
        />
      ) : null}
      {hoursChange ? (
        <ChangeItem 
          label="Working Hours" 
          oldVal={String(hoursChange.old || 0) + "h"} 
          newVal={String(hoursChange.new || 0) + "h"} 
        />
      ) : null}
      {statusChange ? (
        <ChangeItem 
          label="Status" 
          oldVal={statusChange.old || "None"} 
          newVal={statusChange.new || ""} 
        />
      ) : null}
    </div>
  );
}

function ChangeItem({ label, oldVal, newVal }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-slate-500">{label}:</span>
      <span className="text-red-500 line-through">{oldVal}</span>
      <span>-&gt;</span>
      <span className="text-emerald-600">{newVal}</span>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-4 text-slate-500">
      <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
      <p className="text-sm">No edit history found</p>
    </div>
  );
}

export default HistoryDialog;
